<template>
    <!--
        2.6 merge-with-diff confirmation modal. Mounted by AccountSettingsComponent
        when the OAuth callback redirects with `?merge_required=1&merge_id=<nonce>`.

        Wire shape (matches server/src/types/responses/MergeDiffResponse.ts):
          - on mount: GET /users/me/merge-diff?merge_id=<id> → renders the diff
          - on confirm: POST /users/me/confirm-merge { merge_id } → success →
            window.location → /account-settings?linked=<provider>
          - on cancel:  DELETE /users/me/merge-pending/<id> → push back to
            /account-settings (idempotent — endpoint returns 204 whether the
            row existed or not)

        Why redirect (not router.push) on confirm: the server sets / rotates
        the cf_session cookie on the success path eventually; a full nav also
        forces App.vue's mount-time /users/me hydration to re-read the
        post-merge state without stale Vuex.
    -->
    <v-dialog
        :model-value="modelValue"
        max-width="720"
        persistent
        scrollable
        content-class="merge-modal-content"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card class="merge-modal">
            <v-card-title class="merge-modal-title">
                Merge accounts?
            </v-card-title>
            <v-card-text>
                <v-overlay
                    v-model="loading"
                    class="align-center justify-center"
                    contained
                >
                    <v-progress-circular color="white" indeterminate size="64"></v-progress-circular>
                </v-overlay>

                <v-alert v-if="errorMessage" type="error" :text="errorMessage" class="mb-3"></v-alert>

                <template v-if="diff && !errorMessage">
                    <p class="merge-modal-lead">
                        This
                        <strong>{{ providerLabel }}</strong>
                        account is attached to a different ChatFight account
                        that has data. Merging will combine all of it onto
                        your current login.
                    </p>

                    <div class="merge-section">
                        <h3>Items to add</h3>
                        <p v-if="diff.combined.itemsAdded.length === 0" class="empty-line">
                            No new items will be added.
                        </p>
                        <ul v-else class="merge-list">
                            <li
                                v-for="item in diff.combined.itemsAdded"
                                :key="item._id"
                            >
                                {{ item.display_name || item.name }}
                                <span class="muted">({{ item.equipment_type }})</span>
                            </li>
                        </ul>
                        <p
                            v-if="diff.combined.itemsDuplicate.length > 0"
                            class="muted small"
                        >
                            {{ diff.combined.itemsDuplicate.length }} item(s)
                            you already own will be kept once.
                        </p>
                    </div>

                    <div class="merge-section">
                        <h3>Orders to merge in</h3>
                        <p v-if="diff.combined.ordersAdded.length === 0" class="empty-line">
                            No orders to merge.
                        </p>
                        <ul v-else class="merge-list">
                            <li
                                v-for="order in diff.combined.ordersAdded"
                                :key="order._id"
                            >
                                Order {{ order._id }}
                                <span class="muted">
                                    {{ formatDate(order.createdDate) }}
                                    <template v-if="typeof order.total === 'number'">
                                        — {{ formatCurrency(order.total) }}
                                    </template>
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="merge-section">
                        <h3>Stats (combined)</h3>
                        <table class="stats-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Yours</th>
                                    <th>Other</th>
                                    <th>After merge</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="field in STATS_FIELDS" :key="field">
                                    <td>{{ field }}</td>
                                    <td>{{ statValue(diff.survivor.stats, field) }}</td>
                                    <td>{{ statValue(diff.loser.stats, field) }}</td>
                                    <td>
                                        <strong>{{ diff.combined.statsCombined[field] ?? 0 }}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </v-card-text>

            <!--
                Visual hierarchy: "Merge & continue" is the recommended action
                — the user already initiated a link expecting their accounts
                to combine. Filled primary on the right; "Keep them separate"
                drops to text variant so it reads as the back-out option.
                Brand-purple (not Discord blue) because this modal also fires
                for Twitch merges; the color should be provider-agnostic.
            -->
            <v-card-actions class="merge-modal-actions">
                <v-btn
                    variant="text"
                    :disabled="submitting"
                    @click="onCancel"
                >Keep them separate</v-btn>
                <v-btn
                    color="#995BD5"
                    class="text-white"
                    variant="flat"
                    size="large"
                    :loading="submitting"
                    :disabled="loading || !diff || !!errorMessage"
                    @click="onConfirm"
                >Merge &amp; continue</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
// Stats fields surfaced in the side-by-side table. Mirrors the server's
// `SUMMABLE_USERSTATS_FIELDS` in AccountMergeService. Listed explicitly so
// the rendering order is deterministic and a server-side add doesn't
// auto-bleed into the modal until the client is updated.
const STATS_FIELDS = [
    'games_played',
    'damage_done',
    'kills',
    'wins',
    'time_in_game',
    'distance_traveled',
];

export default {
    name: 'MergeConfirmModal',
    props: {
        modelValue: { type: Boolean, default: false },
        mergeId: { type: String, required: true },
        provider: { type: String, default: 'discord' }, // 'twitch' | 'discord'
    },
    emits: ['update:modelValue', 'merged', 'cancelled', 'active-session'],
    data() {
        return {
            loading: true,
            submitting: false,
            errorMessage: '',
            diff: null,
            STATS_FIELDS,
        };
    },
    computed: {
        providerLabel() {
            return this.provider === 'twitch' ? 'Twitch' : 'Discord';
        },
    },
    watch: {
        // Re-fetch the diff if the modal is reopened with a different merge_id
        // (defensive — in normal use the modal is mounted once per redirect).
        mergeId: {
            immediate: true,
            handler() {
                if (this.mergeId) this.fetchDiff();
            },
        },
    },
    methods: {
        async fetchDiff() {
            this.loading = true;
            this.errorMessage = '';
            this.diff = null;
            try {
                const res = await fetch(
                    `/users/me/merge-diff?merge_id=${encodeURIComponent(this.mergeId)}`,
                    { method: 'GET', credentials: 'include' },
                );
                if (res.status === 401) {
                    this.errorMessage = 'You are not signed in. Please log in and try again.';
                    return;
                }
                if (res.status === 404) {
                    this.errorMessage =
                        'This merge request expired or is no longer valid. Try linking again.';
                    return;
                }
                if (!res.ok) {
                    this.errorMessage = `Could not load merge details (HTTP ${res.status}).`;
                    return;
                }
                this.diff = await res.json();
            } catch (err) {
                console.error('MergeConfirmModal: fetchDiff failed', err);
                this.errorMessage = 'Could not load merge details. Try again in a moment.';
            } finally {
                this.loading = false;
            }
        },
        async onConfirm() {
            this.submitting = true;
            try {
                const res = await fetch('/users/me/confirm-merge', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ merge_id: this.mergeId }),
                });
                if (res.status === 409) {
                    // Active battle in flight. Caller renders the
                    // "stop the battle first" banner.
                    this.errorMessage =
                        'Cannot merge while a battle is in progress. End the battle and try again.';
                    this.$emit('active-session');
                    return;
                }
                if (res.status === 404) {
                    this.errorMessage =
                        'This merge request expired. Re-link to try again.';
                    return;
                }
                if (!res.ok) {
                    this.errorMessage = `Merge failed (HTTP ${res.status}).`;
                    return;
                }
                // Success — emit so the parent can navigate / refresh.
                this.$emit('merged', { provider: this.provider });
            } catch (err) {
                console.error('MergeConfirmModal: confirm failed', err);
                this.errorMessage = 'Merge failed. Try again in a moment.';
            } finally {
                this.submitting = false;
            }
        },
        async onCancel() {
            try {
                await fetch(
                    `/users/me/merge-pending/${encodeURIComponent(this.mergeId)}`,
                    { method: 'DELETE', credentials: 'include' },
                );
            } catch (err) {
                // Cancel is idempotent server-side AND a failed cancel here
                // doesn't compromise security — the TTL still sweeps it.
                // Best-effort.
                console.warn('MergeConfirmModal: cancel best-effort fetch failed', err);
            } finally {
                this.$emit('cancelled');
            }
        },
        statValue(stats, field) {
            if (!stats) return 0;
            const v = stats[field];
            return typeof v === 'number' ? v : 0;
        },
        formatDate(value) {
            if (!value) return '';
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return '';
            return d.toLocaleDateString();
        },
        formatCurrency(n) {
            if (typeof n !== 'number') return '';
            return `$${n.toFixed(2)}`;
        },
    },
};
</script>

<style scoped>
.merge-modal {
    background-color: rgba(0, 0, 0, 0.92) !important;
    color: white !important;
}
/*
 * Vuetify v3 (light theme default) applies dark `--v-theme-on-surface`
 * colors directly to .v-card-title / .v-card-text / .v-card-actions
 * which break inheritance from the parent .merge-modal `color: white`.
 * Force white on every inner text container so the modal body is
 * legible against the rgba(0,0,0,0.92) backdrop above.
 */
.merge-modal :deep(.v-card-title),
.merge-modal :deep(.v-card-text),
.merge-modal :deep(.v-card-subtitle),
.merge-modal :deep(.v-card-actions) {
    color: white !important;
}
.merge-modal-title {
    color: white;
    font-size: 1.4em;
}
.merge-modal-lead {
    margin-bottom: 16px;
}
.merge-section {
    margin-top: 18px;
}
.merge-section h3 {
    margin-bottom: 6px;
    font-size: 1.05em;
    color: #cccccc;
}
.merge-list {
    list-style: disc inside;
    padding-left: 6px;
}
.muted {
    opacity: 0.7;
    margin-left: 6px;
}
.small {
    font-size: 0.85em;
}
.empty-line {
    opacity: 0.6;
    font-style: italic;
}
.stats-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95em;
}
.stats-table th,
.stats-table td {
    padding: 4px 8px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.stats-table th {
    color: #cccccc;
    font-weight: 600;
}
.merge-modal-actions {
    padding: 12px 24px 16px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}
</style>

<!--
    Unscoped block on purpose: `contentClass` lands on the teleported
    `.v-overlay__content` (a sibling of <v-app>'s overlay container,
    outside this component's scoped-CSS reach). Vuetify 3.11.2's
    `staticLocationStrategy` is an unimplemented no-op, so dialogs
    without an activator slot get NO inline top/left applied and the
    absolutely-positioned content defaults to top:0;left:0 + the
    framework's 24px margin — visible as the modal nailed to the
    upper-left corner. Override with viewport-centered transform.
    Scoped to .merge-modal-content so it can't bleed onto activator-
    anchored dialogs (e.g. CartItemComponent's trash confirm).
-->
<style>
.merge-modal-content {
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
}
</style>
